import { useEffect, useRef, useState } from 'react';
import { getStatusCopy } from '@/shared/copy/status';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import { MetricValueText } from './StatusMetricCardValueRow';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ReactElement } from 'react';

const renderStatusCardDeltaText = (
  value: MetricCardItem['countDelta'],
  tone: MetricCardItem['countDeltaTone'],
  wrapInBrackets = false,
): ReactElement | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalizedValue =
    typeof value === 'string' && wrapInBrackets ? value.replace(/^[+-]/, '') : value;
  if (normalizedValue === '') return null;
  const content =
    typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
      ? wrapInBrackets && normalizedValue !== '-'
        ? `(${normalizedValue})`
        : normalizedValue
      : normalizedValue;

  return (
    <Text
      role="bodyMuted"
      sx={(muiTheme) => ({
        color:
          tone === 'positive'
            ? 'success.main'
            : tone === 'negative'
              ? 'error.main'
              : 'text.secondary',
        fontSize: muiTheme.typography.subtitle1.fontSize,
        lineHeight: muiTheme.typography.subtitle1.lineHeight,
        whiteSpace: 'nowrap',
      })}
    >
      {content}
    </Text>
  );
};

const StatusMetricCardDeltaPlaceholder = (): ReactElement => (
  <Text
    aria-hidden="true"
    role="bodyMuted"
    sx={(muiTheme) => ({
      visibility: 'hidden',
      fontSize: muiTheme.typography.subtitle1.fontSize,
      lineHeight: muiTheme.typography.subtitle1.lineHeight,
      whiteSpace: 'nowrap',
    })}
  >
    +0 +0%
  </Text>
);

export const StatusMetricCardFooter = ({
  comparisonWindowLabel,
  countDelta,
  countDeltaTone,
  percentageDelta,
  percentageDeltaTone,
  showNoChangePlaceholder,
}: {
  comparisonWindowLabel: string | null;
  countDelta: MetricCardItem['countDelta'];
  countDeltaTone: MetricCardItem['countDeltaTone'];
  percentageDelta: MetricCardItem['percentageDelta'];
  percentageDeltaTone: MetricCardItem['percentageDeltaTone'];
  showNoChangePlaceholder: boolean;
}): ReactElement => {
  const hasCountDelta =
    countDelta !== null && countDelta !== undefined && countDelta !== '' && countDelta !== '-';
  const hasPercentageDelta =
    percentageDelta !== null &&
    percentageDelta !== undefined &&
    percentageDelta !== '' &&
    percentageDelta !== '-';
  const hasDelta = hasCountDelta || hasPercentageDelta;
  const resolvedDeltaTone = countDeltaTone ?? percentageDeltaTone;
  const footerRef = useRef<HTMLDivElement | null>(null);
  const deltaRef = useRef<HTMLDivElement | null>(null);
  const comparisonLabelRef = useRef<HTMLDivElement | null>(null);
  const [stackDeltaContent, setStackDeltaContent] = useState(false);

  useEffect(() => {
    const footerElement = footerRef.current;
    const deltaElement = deltaRef.current;
    const comparisonLabelElement = comparisonLabelRef.current;

    if (!footerElement || !deltaElement) {
      setStackDeltaContent(false);
      return;
    }

    if (!comparisonWindowLabel && !showNoChangePlaceholder) {
      setStackDeltaContent(false);
      return;
    }

    const measureLayout = (): void => {
      const footerStyles = window.getComputedStyle(footerElement);
      const columnGap = Number.parseFloat(footerStyles.columnGap || '0');
      const comparisonWidth =
        comparisonLabelElement === null ? 0 : comparisonLabelElement.scrollWidth;
      const requiredWidth = deltaElement.scrollWidth + comparisonWidth + columnGap;
      setStackDeltaContent(requiredWidth > footerElement.clientWidth);
    };

    measureLayout();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      measureLayout();
    });
    resizeObserver.observe(footerElement);
    if (comparisonLabelElement) {
      resizeObserver.observe(comparisonLabelElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    comparisonWindowLabel,
    countDelta,
    countDeltaTone,
    percentageDelta,
    percentageDeltaTone,
    showNoChangePlaceholder,
  ]);

  return (
    <Box
      ref={footerRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'end',
        columnGap: s('md'),
        rowGap: s('none'),
        minWidth: 0,
      }}
    >
      <Box
        ref={deltaRef}
        sx={{
          display: 'flex',
          flexDirection: stackDeltaContent ? 'column' : 'row',
          alignItems: stackDeltaContent ? 'flex-start' : 'flex-end',
          flexWrap: stackDeltaContent ? 'nowrap' : 'wrap',
          gap: s('sm'),
          rowGap: s('xxs'),
          pl: s('none'),
          minWidth: 0,
        }}
      >
        {hasDelta ? (
          <>
            {renderStatusCardDeltaText(countDelta, resolvedDeltaTone)}
            {renderStatusCardDeltaText(percentageDelta, resolvedDeltaTone)}
          </>
        ) : (
          <StatusMetricCardDeltaPlaceholder />
        )}
      </Box>
      {comparisonWindowLabel ? (
        <Box
          ref={comparisonLabelRef}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            alignSelf: 'end',
            justifySelf: 'end',
            minHeight: '100%',
            minWidth: 0,
            textAlign: 'right',
          }}
        >
          <MetricValueText nowrap align="right" fontSize="16px" lineHeight={1.2}>
            {comparisonWindowLabel}
          </MetricValueText>
        </Box>
      ) : !hasDelta && showNoChangePlaceholder ? (
        <Box
          ref={comparisonLabelRef}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            alignSelf: 'end',
            justifySelf: 'end',
            minHeight: '100%',
            minWidth: 0,
            textAlign: 'right',
          }}
        >
          <MetricValueText nowrap align="right" fontSize="16px" lineHeight={1.2}>
            {getStatusCopy('noChange')}
          </MetricValueText>
        </Box>
      ) : (
        <Box />
      )}
    </Box>
  );
};
